import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useState,
} from 'react';

import { ContactSteps } from '@/constants/ecomm/steps';
import { MarketInfo } from '@/types/ecomm/marketInfo';
import { OffsetKey, Products } from '@/types/ecomm/products';

import type { ECommQuoteContextType, Props } from './types';

const eCommQuoteContextDefaultValues: ECommQuoteContextType = {
  avgMonthlyBill: undefined,
  setAvgMonthlyBill: () => {
    throw new Error('Function not implemented.');
  },
  availableUtilities: [],
  setAvailableUtilities: () => {
    throw new Error('Function not implemented.');
  },
  selectedUtility: '',
  setSelectedUtility: () => {
    throw new Error('Function not implemented.');
  },
  selectedBatteryOptionIndex: undefined,
  setSelectedBatteryOptionIndex: () => {
    throw new Error('Function not implemented.');
  },
  products: undefined,
  setProducts: () => {
    throw new Error('Function not implemented.');
  },
  selectedOffsetKey: undefined,
  setSelectedOffsetKey: () => {
    throw new Error('Function not implemented.');
  },
  firstName: undefined,
  setFirstName: () => {
    throw new Error('Function not implemented.');
  },
  lastName: undefined,
  setLastName: () => {
    throw new Error('Function not implemented.');
  },
  phone: undefined,
  setPhone: () => {
    throw new Error('Function not implemented.');
  },
  email: undefined,
  setEmail: () => {
    throw new Error('Function not implemented.');
  },
  zipCode: undefined,
  setZipCode: () => {
    throw new Error('Function not implemented.');
  },
  state: undefined,
  setState: () => {
    throw new Error('Function not implemented.');
  },
  ownsHome: false,
  setOwnsHome: () => {
    throw new Error('Function not implemented.');
  },
  acceptedToS: false,
  setAcceptedToS: () => {
    throw new Error('Function not implemented.');
  },
  currentStep: ContactSteps?.CUSTOMIZE,
  setCurrentStep: () => {
    throw new Error('Function not implemented');
  },
  maxOffset: 1,
  setMaxOffset: () => {
    throw new Error('Function not implemented');
  },
  statesAllowed: [],
  setStatesAllowed: () => {
    throw new Error('Function not implemented.');
  },
  loadingVisible: false,
  loadingText: undefined,
  showLoading: () => {
    throw new Error('Function not implemented.');
  },
  hideLoading: () => {
    throw new Error('Function not implemented.');
  },
  resetContext: () => {
    throw new Error('Function not implemented.');
  },
  featureFlags: undefined,
  setFeatureFlags: () => {
    throw new Error('Function not implemented.');
  },
  marketInfo: undefined,
  setMarketInfo: () => {
    throw new Error('Function not implemented.');
  },
  ecommReferrerPage: undefined,
  setEcommReferrerPage: () => {
    throw new Error('Function not implemented.');
  },
  kioskTimeout: undefined,
  setKioskTimeout: () => {
    throw new Error('Function not implemented.');
  },
};

const ECommQuoteContext = createContext<ECommQuoteContextType>(
  eCommQuoteContextDefaultValues
);

export const useECommQuoteContext = () => {
  return useContext(ECommQuoteContext);
};

// testValues prop should only be used to mock out context values in tests
export const ECommQuoteProvider: FC<Props> = ({
  children,
  initialProps,
  testValues,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  const getDefaultState = <T extends unknown>(
    key: string,
    defaultValue: T,
    override: boolean
  ): T => {
    let currentValue: unknown;
    if (!override && typeof window !== 'undefined') {
      currentValue = window.localStorage.getItem(key);

      if (currentValue === null) {
        window.localStorage.setItem(key, JSON.stringify(defaultValue));
      } else if (typeof defaultValue === 'boolean') {
        currentValue = currentValue === 'true';
      } else if (currentValue === 'undefined') {
        currentValue = undefined;
      } else {
        try {
          currentValue = JSON.parse(currentValue as string);
        } catch (err) {
          console.warn(`Error setting up default state in context: ${err}`);
          currentValue = undefined;
        }
      }
    }

    return (currentValue ?? defaultValue) as T;
  };

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  const setECommQuoteState = <T extends unknown>(
    key: string,
    updatedValue: T,
    setter: Dispatch<SetStateAction<T>>
  ): void => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(key, JSON.stringify(updatedValue));
    }

    return setter(updatedValue);
  };

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
  const useECommQuoteState = <T extends unknown>(
    key: string,
    defaultValue: T,
    override = false
  ) => {
    const [stateVar, setter] = useState<T>(
      getDefaultState<T>(key, defaultValue, override)
    );
    return [
      stateVar,
      (updatedValue: T) => setECommQuoteState(key, updatedValue, setter),
    ] as const;
  };

  const [avgMonthlyBill, setAvgMonthlyBill] = useECommQuoteState<
    number | undefined
  >('avgMonthlyBill', eCommQuoteContextDefaultValues.avgMonthlyBill);
  const [selectedBatteryOptionIndex, setSelectedBatteryOptionIndex] =
    useECommQuoteState<number | undefined>(
      'selectedBatteryOptionIndex',
      eCommQuoteContextDefaultValues.selectedBatteryOptionIndex
    );
  const [products, setProducts] = useECommQuoteState<Products | undefined>(
    'products',
    eCommQuoteContextDefaultValues.products
  );
  const [selectedOffsetKey, setSelectedOffsetKey] = useECommQuoteState<
    OffsetKey | undefined
  >('selectedOffsetKey', eCommQuoteContextDefaultValues.selectedOffsetKey);
  const [firstName, setFirstName] = useECommQuoteState<string | undefined>(
    'firstName',
    eCommQuoteContextDefaultValues.firstName
  );
  const [lastName, setLastName] = useECommQuoteState<string | undefined>(
    'lastName',
    eCommQuoteContextDefaultValues.lastName
  );
  const [phone, setPhone] = useECommQuoteState<string | undefined>(
    'phone',
    eCommQuoteContextDefaultValues.phone
  );
  const [email, setEmail] = useECommQuoteState<string | undefined>(
    'email',
    eCommQuoteContextDefaultValues.email
  );
  const [zipCode, setZipCode] = useECommQuoteState<string | undefined>(
    'zipCode',
    eCommQuoteContextDefaultValues.zipCode
  );
  const [state, setState] = useECommQuoteState<string | undefined>(
    'state',
    eCommQuoteContextDefaultValues.state
  );
  const [ownsHome, setOwnsHome] = useECommQuoteState<boolean>(
    'ownsHome',
    eCommQuoteContextDefaultValues.ownsHome
  );
  const [acceptedToS, setAcceptedToS] = useECommQuoteState<boolean>(
    'acceptedToS',
    eCommQuoteContextDefaultValues.acceptedToS
  );
  const [availableUtilities, setAvailableUtilities] = useECommQuoteState<
    Array<string>
  >('availableUtilities', eCommQuoteContextDefaultValues.availableUtilities);
  const [selectedUtility, setSelectedUtility] = useECommQuoteState<string>(
    'selectedUtility',
    eCommQuoteContextDefaultValues.selectedUtility
  );
  const [currentStep, setCurrentStep] = useECommQuoteState<ContactSteps>(
    'currentStep',
    eCommQuoteContextDefaultValues.currentStep
  );
  const [maxOffset, setMaxOffset] = useECommQuoteState<number>(
    'maxOffset',
    eCommQuoteContextDefaultValues.maxOffset
  );
  const [statesAllowed, setStatesAllowed] = useECommQuoteState<Array<string>>(
    'statesAllowed',
    eCommQuoteContextDefaultValues.statesAllowed
  );

  const [loadingVisible, setLoadingVisible] = useState<boolean>(
    eCommQuoteContextDefaultValues.loadingVisible
  );

  const [loadingText, setLoadingText] = useState<string | undefined>(
    eCommQuoteContextDefaultValues.loadingText
  );

  const [featureFlags, setFeatureFlags] = useState(
    eCommQuoteContextDefaultValues.featureFlags
  );

  const [marketInfo, setMarketInfo] = useECommQuoteState<
    MarketInfo | undefined
  >('marketInfo', eCommQuoteContextDefaultValues.marketInfo);

  const [ecommReferrerPage, setEcommReferrerPage] = useECommQuoteState<
    string | undefined
  >('ecommReferrerPage', eCommQuoteContextDefaultValues.ecommReferrerPage);

  const [kioskTimeout, setKioskTimeout] = useECommQuoteState<
    number | undefined
  >('kioskTimeout', eCommQuoteContextDefaultValues.kioskTimeout);

  const showLoading = (loadingText?: string) => {
    setLoadingText(loadingText);
    setLoadingVisible(true);
  };

  const hideLoading = () => {
    setLoadingVisible(false);
    setLoadingText(undefined);
  };

  const resetContext = () => {
    setAvgMonthlyBill(eCommQuoteContextDefaultValues.avgMonthlyBill);
    setSelectedBatteryOptionIndex(
      eCommQuoteContextDefaultValues.selectedBatteryOptionIndex
    );
    setProducts(eCommQuoteContextDefaultValues.products);
    setSelectedOffsetKey(eCommQuoteContextDefaultValues.selectedOffsetKey);
    setFirstName(eCommQuoteContextDefaultValues.firstName);
    setLastName(eCommQuoteContextDefaultValues.lastName);
    setPhone(eCommQuoteContextDefaultValues.phone);
    setEmail(eCommQuoteContextDefaultValues.email);
    setZipCode(eCommQuoteContextDefaultValues.zipCode);
    setState(eCommQuoteContextDefaultValues.state);
    setOwnsHome(eCommQuoteContextDefaultValues.ownsHome);
    setAcceptedToS(eCommQuoteContextDefaultValues.acceptedToS);
    setCurrentStep(eCommQuoteContextDefaultValues.currentStep);
    setSelectedUtility(eCommQuoteContextDefaultValues.selectedUtility);
    setAvailableUtilities(eCommQuoteContextDefaultValues.availableUtilities);
    setStatesAllowed(eCommQuoteContextDefaultValues.statesAllowed);
    setMarketInfo(eCommQuoteContextDefaultValues.marketInfo);
    setEcommReferrerPage(eCommQuoteContextDefaultValues.ecommReferrerPage);
    setKioskTimeout(eCommQuoteContextDefaultValues.kioskTimeout);
  };

  return (
    <ECommQuoteContext.Provider
      value={{
        avgMonthlyBill,
        setAvgMonthlyBill,
        selectedBatteryOptionIndex,
        setSelectedBatteryOptionIndex,
        products,
        setProducts,
        selectedOffsetKey,
        setSelectedOffsetKey,
        firstName,
        setFirstName,
        lastName,
        setLastName,
        phone,
        setPhone,
        email,
        setEmail,
        zipCode,
        setZipCode,
        state,
        setState,
        ownsHome,
        setOwnsHome,
        acceptedToS,
        setAcceptedToS,
        currentStep,
        setCurrentStep,
        availableUtilities,
        setAvailableUtilities,
        selectedUtility,
        setSelectedUtility,
        maxOffset,
        setMaxOffset,
        statesAllowed,
        setStatesAllowed,
        loadingVisible,
        loadingText,
        showLoading,
        hideLoading,
        resetContext,
        featureFlags,
        setFeatureFlags,
        marketInfo,
        setMarketInfo,
        ecommReferrerPage,
        setEcommReferrerPage,
        kioskTimeout,
        setKioskTimeout,
        ...initialProps,
        ...testValues,
      }}
    >
      {children}
    </ECommQuoteContext.Provider>
  );
};
